import { useEffect } from 'react'

import { fetchAvailableRatesForEachRoom, fetchAvailableRatesForOffer } from 'actions/OfferActions'
import { useAppDispatch } from 'hooks/reduxHooks'

interface Options {
  offer: App.HotelOffer
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
  flightOrigin?: string
  enabled: boolean
  fetchForEachRoom?: boolean
}

/**
 * Loads the bookable Flash hotel rates for an exact stay.
 *
 * Calendar prices are sufficient for browsing, but hotels with dynamic
 * cancellation policies need this request before a dated room rate is treated
 * as the final price and policy. Keeping the request here prevents offer-page
 * surfaces from drifting on when and how that transition happens.
 */
function useFlashHotelAvailableRates({
  offer,
  checkIn,
  checkOut,
  rooms,
  flightOrigin,
  enabled,
  fetchForEachRoom = false,
}: Options) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    const occupanciesInvalid = rooms.some(room => room.childrenAge?.some(age => age < 0))
    if (
      !enabled ||
      !checkIn ||
      !checkOut ||
      occupanciesInvalid ||
      !offer.property.useDynamicCancellationPolicies
    ) return

    const params = {
      occupants: rooms,
      checkIn,
      checkOut,
      flightOrigin,
    }

    if (fetchForEachRoom) {
      dispatch(fetchAvailableRatesForEachRoom(offer, params))
    } else {
      dispatch(fetchAvailableRatesForOffer(offer, params))
    }
  }, [checkIn, checkOut, dispatch, enabled, fetchForEachRoom, flightOrigin, offer, rooms])
}

export default useFlashHotelAvailableRates
